/**
 * 用户管理页面（仅限 admin）
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../services/auth';
import apiClient from '../services/api';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import EmptyState from '../components/EmptyState';
import './UserManagement.css';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBindingModal, setShowBindingModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBindings, setUserBindings] = useState([]);
  
  const [userForm, setUserForm] = useState({ username: '', password: '', name: '' });
  const [bindingForm, setBindingForm] = useState({ device_id: '', wifi_ssid: '' });

  // 权限检查
  useEffect(() => {
    const user = getCurrentUser();
    if (!user || user.username !== 'admin') {
      navigate('/games');
    }
  }, [navigate]);

  // 加载用户列表
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/users');
      setUsers(response.data.code === 0 ? response.data.data || [] : []);
      setError(response.data.code === 0 ? '' : response.data.message);
    } catch (err) {
      setError(err.response?.data?.message || '加载用户列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // 用户操作
  const openCreateModal = () => {
    setEditingUser(null);
    setUserForm({ username: '', password: '', name: '' });
    setShowUserModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setUserForm({ username: user.username, password: '', name: user.name });
    setShowUserModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const data = {};
        if (userForm.username !== editingUser.username) data.username = userForm.username;
        if (userForm.name !== editingUser.name) data.name = userForm.name;
        if (userForm.password) data.password = userForm.password;
        await apiClient.put(`/admin/users/${editingUser.id}`, data);
      } else {
        await apiClient.post('/admin/users', userForm);
      }
      setShowUserModal(false);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.message || '保存失败');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.username === 'admin') return alert('不能删除管理员账户');
    if (!window.confirm(`确定要删除用户 "${user.name}" 吗？`)) return;
    
    try {
      await apiClient.delete(`/admin/users/${user.id}`);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.message || '删除失败');
    }
  };

  // 绑定操作
  const openBindingModal = async (user) => {
    setSelectedUser(user);
    setBindingForm({ device_id: '', wifi_ssid: '' });
    try {
      const response = await apiClient.get(`/admin/users/${user.id}/bindings`);
      setUserBindings(response.data.code === 0 ? response.data.data || [] : []);
    } catch {
      setUserBindings([]);
    }
    setShowBindingModal(true);
  };

  const handleAddBinding = async (e) => {
    e.preventDefault();
    if (!bindingForm.device_id.trim()) return alert('请输入设备ID');
    
    try {
      await apiClient.post(`/admin/users/${selectedUser.id}/bindings`, bindingForm);
      const response = await apiClient.get(`/admin/users/${selectedUser.id}/bindings`);
      setUserBindings(response.data.code === 0 ? response.data.data || [] : []);
      setBindingForm({ device_id: '', wifi_ssid: '' });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.message || '添加绑定失败');
    }
  };

  const handleDeleteBinding = async (deviceId) => {
    if (!window.confirm(`确定要解除设备 "${deviceId}" 的绑定吗？`)) return;
    
    try {
      await apiClient.delete(`/admin/users/${selectedUser.id}/bindings/${deviceId}`);
      const response = await apiClient.get(`/admin/users/${selectedUser.id}/bindings`);
      setUserBindings(response.data.code === 0 ? response.data.data || [] : []);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.message || '删除绑定失败');
    }
  };

  const getAvatarLetter = (name) => name?.charAt(0).toUpperCase() || '?';

  if (loading) {
    return (
      <div className="page-container">
        <header className="page-header">
          <button className="btn-back" onClick={() => navigate(-1)}>←</button>
          <div className="page-header-title"><h1>用户管理</h1></div>
          <div style={{ width: 40 }}></div>
        </header>
        <Loading text="加载中..." />
      </div>
    );
  }

  return (
    <div className="page-container user-management">
      <header className="page-header">
        <button className="btn-back" onClick={() => navigate(-1)}>←</button>
        <div className="page-header-title">
          <h1>用户管理</h1>
          <p>共 {users.length} 位用户</p>
        </div>
        <button className="btn btn-sm add-user-btn" onClick={openCreateModal}>+ 添加</button>
      </header>

      {error && <div className="error-box">{error}</div>}

      <div className="content-area">
        {users.length === 0 ? (
          <div className="card">
            <EmptyState icon="👥" title="暂无用户" text="点击右上角添加第一位用户" />
          </div>
        ) : (
          <div className="user-cards">
            {users.map(user => (
              <div className="user-card card" key={user.id}>
                <div className="user-card-header">
                  <div className={`user-avatar ${user.username === 'admin' ? 'admin' : ''}`}>
                    {getAvatarLetter(user.name)}
                  </div>
                  <div className="user-info">
                    <h3 className="user-name">{user.name}</h3>
                    <p className="user-username">@{user.username}</p>
                  </div>
                  <span className={`user-badge ${user.username === 'admin' ? 'admin' : 'user'}`}>
                    {user.username === 'admin' ? '管理员' : '用户'}
                  </span>
                </div>
                
                <div className="user-card-body" onClick={() => openBindingModal(user)}>
                  <span className="device-icon">📱</span>
                  <div className="device-text">
                    <span className="device-count">{user.device_ids?.length || 0}</span>
                    <span className="device-label">绑定设备</span>
                  </div>
                </div>

                <div className="user-card-actions">
                  <button className="action-btn edit" onClick={() => openEditModal(user)}>✏️ 编辑</button>
                  <button className="action-btn binding" onClick={() => openBindingModal(user)}>🔗 绑定</button>
                  {user.username !== 'admin' && (
                    <button className="action-btn delete" onClick={() => handleDeleteUser(user)}>🗑️ 删除</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 用户编辑弹窗 */}
      <Modal
        visible={showUserModal}
        title={editingUser ? '编辑用户' : '添加用户'}
        subtitle={editingUser ? '修改用户信息' : '创建新的用户账户'}
        onClose={() => setShowUserModal(false)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowUserModal(false)}>取消</button>
            <button className="btn btn-primary" onClick={handleSaveUser}>保存</button>
          </>
        }
      >
        <form onSubmit={handleSaveUser}>
          <div className="form-group">
            <label className="form-label">用户名</label>
            <input
              type="text"
              className="form-input"
              placeholder="请输入用户名"
              value={userForm.username}
              onChange={e => setUserForm({...userForm, username: e.target.value})}
              required
              minLength={3}
              disabled={editingUser?.username === 'admin'}
            />
          </div>
          <div className="form-group">
            <label className="form-label">姓名</label>
            <input
              type="text"
              className="form-input"
              placeholder="请输入姓名"
              value={userForm.name}
              onChange={e => setUserForm({...userForm, name: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">{editingUser ? '新密码（留空不修改）' : '密码'}</label>
            <input
              type="password"
              className="form-input"
              placeholder={editingUser ? '留空则不修改密码' : '请输入密码（至少6位）'}
              value={userForm.password}
              onChange={e => setUserForm({...userForm, password: e.target.value})}
              required={!editingUser}
              minLength={6}
            />
          </div>
        </form>
      </Modal>

      {/* 设备绑定弹窗 */}
      <Modal
        visible={showBindingModal && !!selectedUser}
        title="设备绑定"
        subtitle={`${selectedUser?.name} 的绑定设备`}
        onClose={() => setShowBindingModal(false)}
        className="binding-modal"
        footer={<button className="btn btn-secondary" onClick={() => setShowBindingModal(false)}>关闭</button>}
      >
        <div className="binding-list">
          {userBindings.length === 0 ? (
            <p className="no-bindings">暂无绑定设备</p>
          ) : (
            <ul>
              {userBindings.map(binding => (
                <li key={binding.device_id} className="binding-item">
                  <span className="binding-item-icon">📱</span>
                  <div className="binding-item-info">
                    <div className="binding-device-id">{binding.device_id}</div>
                    {binding.wifi_ssid && <div className="binding-wifi-ssid">WiFi: {binding.wifi_ssid}</div>}
                  </div>
                  <button className="binding-remove-btn" onClick={() => handleDeleteBinding(binding.device_id)}>×</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="add-binding-section">
          <h4>添加新绑定</h4>
          <form className="add-binding-form" onSubmit={handleAddBinding}>
            <input
              type="text"
              className="form-input"
              placeholder="设备ID"
              value={bindingForm.device_id}
              onChange={e => setBindingForm({...bindingForm, device_id: e.target.value})}
            />
            <input
              type="text"
              className="form-input"
              placeholder="WiFi名称（可选）"
              value={bindingForm.wifi_ssid}
              onChange={e => setBindingForm({...bindingForm, wifi_ssid: e.target.value})}
            />
            <button type="submit" className="btn btn-primary">添加绑定</button>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement;
